import React, { useState, useEffect, useRef, useContext } from 'react';
import Select from 'react-select';
import axios from 'axios';
import styles from '../SingleListingGeneratorPage.module.css';
import coreStyles from './DetailsForm.module.css';
import AuthContext from '../../../context/AuthContext';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/single-gen';

function getAttributeValueEnum(propDef) {
  const valueSchema = propDef?.items?.properties?.value;
  if (!valueSchema) return null;
  if (Array.isArray(valueSchema.enum)) return valueSchema.enum;
  if (Array.isArray(valueSchema.anyOf)) {
    for (const branch of valueSchema.anyOf) {
      if (Array.isArray(branch.enum) && branch.enum.length > 0) return branch.enum;
    }
  }
  return null;
}

/** Extract variation themes + per-attribute value enums from the schema. */
function extractFilteredEnums(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const findIn = (o) => {
    if (!o || typeof o !== 'object') return null;
    if (o.properties?.variation_theme?.items?.properties?.name) {
      const nameObj = o.properties.variation_theme.items.properties.name;
      const deprecated = nameObj?.$lifecycle?.enumDeprecated ?? nameObj?.enumDeprecated;
      const enumArr = nameObj?.enum;
      if (Array.isArray(enumArr) && Array.isArray(deprecated)) {
        const deprecatedSet = new Set(deprecated);
        const themes = enumArr.filter((e) => !deprecatedSet.has(e));
        const attributeEnums = {};
        for (const [key, propDef] of Object.entries(o.properties ?? {})) {
          if (key === 'variation_theme') continue;
          const vals = getAttributeValueEnum(propDef);
          if (vals !== null) attributeEnums[key] = vals;
        }
        return { enumDeprecated: deprecated, enum: themes, attributeEnums };
      }
    }
    if (Array.isArray(o)) {
      for (const x of o) {
        const f = findIn(x);
        if (f) return f;
      }
    } else {
      for (const v of Object.values(o)) {
        const f = findIn(v);
        if (f) return f;
      }
    }
    return null;
  };
  return findIn(obj);
}

// Browsenode field component with auto-resize
const BrowsenodeField = ({ value }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight (content height)
      const scrollHeight = textarea.scrollHeight;
      // Ensure minimum height of 48px for single line
      textarea.style.height = `${Math.max(scrollHeight, 48)}px`;
    }
  }, [value]);

  return (
    <div className={coreStyles.fieldRow}>
      <div className={`${coreStyles.fieldContainer} ${coreStyles.fieldContainerExisting}`}>
        <div className={coreStyles.labelContainer}>
          <div className={coreStyles.labelText}>Browse Node</div>
        </div>
        <div className={`${coreStyles.inputContainer} ${coreStyles.browsenodeInputContainer}`}>
          <textarea
            ref={textareaRef}
            value={value}
            disabled
            className={coreStyles.browsenodeField}
            readOnly
            rows={1}
          />
        </div>
      </div>
    </div>
  );
};

const DetailsForm = ({ formData, handleChange, isLoading, listingMode, setFormData, isExpanded, onAddFeatures, dimensionData, setDimensionData, asinProductData, invalidFields = new Set() }) => {
  const { token } = useContext(AuthContext);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchingProductTypes, setIsSearchingProductTypes] = useState(false);
  const [selectedProductOption, setSelectedProductOption] = useState(null); // Store the exact selected option

  // Product Identity Mode specific state
  const [hasEAN, setHasEAN] = useState(formData.eanNumber ? true : null);
  const [hasGTIN, setHasGTIN] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [bannedKeywordInput, setBannedKeywordInput] = useState('');
  const [keywordTags, setKeywordTags] = useState([]);
  const [bannedKeywordTags, setBannedKeywordTags] = useState([]);

  // Initialize hasEAN based on eanNumber
  useEffect(() => {
    if (formData.eanNumber) {
      setHasEAN(true);
    }
  }, [formData.eanNumber]);

  // Debounced search effect - fetch product types from backend SP-API proxy
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setProductTypeOptions([]);
      setIsSearchingProductTypes(false);
      return;
    }

    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      const fetchProductTypes = async () => {
        try {
          setIsSearchingProductTypes(true);
          const response = await axios.get(`${API_URL}/product-types/search`, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            params: {
              keywords: searchQuery.trim(),
            },
            signal: controller.signal,
          });

          const data = response.data || {};
          if (!data.success || !data.data) {
            setProductTypeOptions([]);
            setIsSearchingProductTypes(false);
            return;
          }

          // Try to extract productTypes list from SP-API style response
          const productTypes =
            data.data.productTypes ||
            data.data.product_types ||
            data.data ||
            [];

          const options = (productTypes || [])
            .map((pt) => {
              const name =
                pt.name ||
                pt.productType ||
                pt.product_type ||
                pt.productTypeName ||
                pt.product_type_name;

              if (!name) return null;

              return {
                value: name,
                label: name,
                // We no longer have category hierarchy from local JSON,
                // so leave these empty for now.
                category: '',
                subCategory: '',
                path: name,
              };
            })
            .filter(Boolean);

          setProductTypeOptions(options);
          setIsSearchingProductTypes(false);
        } catch (error) {
          if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return;
          }
          setProductTypeOptions([]);
          setIsSearchingProductTypes(false);
        }
      };

      fetchProductTypes();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      setIsSearchingProductTypes(false);
    };
  }, [searchQuery, token]);

  const handleSelectChange = (name, selectedOption) => {
    if (name === 'productType' && selectedOption) {
      const productTypeValue = selectedOption.value;

      // Store the exact selected option for display
      setSelectedProductOption(selectedOption);

      // When product type is selected, automatically set category and subcategory
      setFormData((prev) => ({
        ...prev,
        productType: productTypeValue,
        category: selectedOption.category,
        subCategory: selectedOption.subCategory,
      }));

      // Fetch product type definition and store only filtered enums for variants step
      axios
        .get(`${API_URL}/product-types/${encodeURIComponent(productTypeValue)}/definition`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        .then((res) => {
          if (res.data?.success && res.data?.data) {
            const filtered = extractFilteredEnums(res.data.data);
            if (filtered?.enum) {
              setFormData((prev) => ({
                ...prev,
                productTypeFilteredEnums: filtered.enum,
                productTypeAttributeEnums: filtered.attributeEnums ?? {},
              }));
            }
          }
        })
        .catch(() => {});

      setSearchQuery('');
      setProductTypeOptions([]);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: selectedOption?.value || ''
      }));
    }
  };

  // Get the currently selected option for display
  const getSelectedOption = () => selectedProductOption || null;

  // Product Identity Mode helper functions – commit current input as tags (on Enter or blur)
  const commitKeywordTags = () => {
    if (!keywordInput.trim()) return;
    const inputTags = keywordInput.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    const newTags = [...keywordTags];
    inputTags.forEach(tag => {
      if (!newTags.includes(tag)) newTags.push(tag);
    });
    if (newTags.length > keywordTags.length) {
      setKeywordTags(newTags);
      setFormData(prev => ({ ...prev, keywords: newTags.join(', ') }));
    }
    setKeywordInput('');
  };

  const addKeywordTag = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitKeywordTags();
    }
  };

  const removeKeywordTag = (tagToRemove) => {
    const newTags = keywordTags.filter(tag => tag !== tagToRemove);
    setKeywordTags(newTags);
    setFormData(prev => ({ ...prev, keywords: newTags.join(', ') }));
  };

  const commitBannedKeywordTags = () => {
    if (!bannedKeywordInput.trim()) return;
    const inputTags = bannedKeywordInput.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    const newTags = [...bannedKeywordTags];
    inputTags.forEach(tag => {
      if (!newTags.includes(tag)) newTags.push(tag);
    });
    if (newTags.length > bannedKeywordTags.length) {
      setBannedKeywordTags(newTags);
      setFormData(prev => ({ ...prev, bannedKeywords: newTags.join(', ') }));
    }
    setBannedKeywordInput('');
  };

  const addBannedKeywordTag = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitBannedKeywordTags();
    }
  };

  const removeBannedKeywordTag = (tagToRemove) => {
    const newTags = bannedKeywordTags.filter(tag => tag !== tagToRemove);
    setBannedKeywordTags(newTags);
    setFormData(prev => ({ ...prev, bannedKeywords: newTags.join(', ') }));
  };

  // Initialize tags from formData
  useEffect(() => {
    if (formData.keywords && keywordTags.length === 0) {
      setKeywordTags(formData.keywords.split(',').map(k => k.trim()).filter(Boolean));
    }
    if (formData.bannedKeywords && bannedKeywordTags.length === 0) {
      setBannedKeywordTags(formData.bannedKeywords.split(',').map(k => k.trim()).filter(Boolean));
    }
  }, [formData.keywords, formData.bannedKeywords]);

  // Custom option renderer to show hierarchical paths
  const customOptionRenderer = ({ data, innerProps, isFocused }) => (
    <div
      {...innerProps}
      className={`${styles.dropdownOption} ${isFocused ? styles.dropdownOptionFocused : ''}`}
    >
      <div className={styles.dropdownOptionLabel}>
        {data.label}
      </div>
      <div className={styles.dropdownOptionPath}>
        {data.path}
      </div>
    </div>
  );
  

  // Custom single value renderer
  const customSingleValueRenderer = (props) => {
    const { data, selectProps } = props;
  
    return (
      <div
        onClick={() => {
          setSelectedProductOption(null);
          setFormData((prev) => ({
            ...prev,
            productType: '',
            category: '',
            subCategory: '',
            productTypeFilteredEnums: undefined,
            productTypeAttributeEnums: undefined
          }));
          // Focus the input field
          selectProps.onMenuOpen();
        }}
        className={styles.dropdownSingleValue}
        style={{ cursor: 'pointer' }}
      >
        <span className={styles.dropdownSingleValueLabel}>{data.label}</span>
        <span className={styles.dropdownSingleValuePath}>{data.path}</span>
      </div>
    );
  };
  

  // Main UI - styled like ProductIdentity Details
  return (
    <div className={coreStyles.sectionWrapper}>
      <div className={coreStyles.contentWrapper}>

          {/* Product Type - Top and full-width */}
          {listingMode === 'new' && (
            <div className={coreStyles.productTypeContainer}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>Product Type<span className={coreStyles.requiredAsterisk}>*</span></div>
                {invalidFields.has('productType') && <span className={coreStyles.requiredLabel}>required*</span>}
              </div>
              <div className={coreStyles.productTypeInputContainer}>
                <Select
                  inputId="productType-default"
                  name="productType"
                  options={productTypeOptions}
                  onChange={(option) => handleSelectChange('productType', option)}
                  value={getSelectedOption()}
                  isSearchable
                  isDisabled={isLoading}
                  placeholder="Type to search product types..."
                  classNamePrefix="react-select"
                  components={{
                    Option: customOptionRenderer,
                    SingleValue: customSingleValueRenderer
                  }}
                  onInputChange={(newValue) => setSearchQuery(newValue)}
                  inputValue={searchQuery}
                  filterOption={() => true}
                  noOptionsMessage={() => {
                    if (!searchQuery || searchQuery.trim().length < 3) {
                      return "Type at least 3 characters to search.";
                    }
                    if (isSearchingProductTypes) {
                      return "Searching...";
                    }
                    return "No product types found. Try a different search term.";
                  }}
                  hideSelectedOptions={true}
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: '100%',
                      flex: 1
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: '40px',
                      width: '100%'
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: '0 4px'
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: 0,
                      padding: 0,
                      display: getSelectedOption() ? 'none' : 'block'
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#737373'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 1000,
                      maxHeight: '400px'
                    })
                  }}
                />
              </div>
            </div>
          )}

          {/* Browsenode (from ASIN data) */}
          {asinProductData?.categories_flat && listingMode === 'existing' && (
            <BrowsenodeField value={asinProductData.categories_flat} />
          )}

          {/* Row 1: Brand Name, Model name, SKU ID, Color */}
          <div className={coreStyles.fieldRow}>
            {/* Brand Name */}
            <div className={`${coreStyles.fieldContainer} ${listingMode === 'existing' ? coreStyles.fieldContainerExisting : ''}`}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>Brand Name<span className={coreStyles.requiredAsterisk}>*</span></div>
                {invalidFields.has('brandName') && <span className={coreStyles.requiredLabel}>required*</span>}
              </div>
              <div className={`${coreStyles.inputContainer} ${coreStyles.inputContainerFlex}`}>
                <input
                  name="brandName"
                  type="text"
                  value={formData.brandName}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Name Of Your Brand"
                  required
                  className={coreStyles.inputField}
                />
              </div>
            </div>

            {/* Model name */}
            <div className={`${coreStyles.fieldContainer} ${listingMode === 'existing' ? coreStyles.fieldContainerExisting : ''}`}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>Model name<span className={coreStyles.requiredAsterisk}>*</span></div>
                {invalidFields.has('modelNumber') && <span className={coreStyles.requiredLabel}>required*</span>}
              </div>
              <div className={coreStyles.inputContainer}>
                <input
                  name="modelNumber"
                  type="text"
                  value={formData.modelNumber}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="e.g. Pro Max 15"
                  required
                  className={`${coreStyles.inputField} ${coreStyles.inputFieldFull}`}
                />
              </div>
            </div>

            {/* SKU ID */}
            <div className={`${coreStyles.fieldContainer} ${listingMode === 'existing' ? coreStyles.fieldContainerExisting : ''}`}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>SKU ID<span className={coreStyles.requiredAsterisk}>*</span></div>
                {invalidFields.has('skuId') && <span className={coreStyles.requiredLabel}>required*</span>}
              </div>
              <div className={coreStyles.inputContainer}>
                <input
                  name="skuId"
                  type="text"
                  value={formData.skuId}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Unique SKU or code"
                  required
                  className={`${coreStyles.inputField} ${coreStyles.inputFieldFull}`}
                />
              </div>
            </div>

            {/* Color */}
            <div className={`${coreStyles.fieldContainer} ${listingMode === 'existing' ? coreStyles.fieldContainerExisting : ''}`}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>Color</div>
              </div>
              <div className={coreStyles.inputContainer}>
                <input
                  name="color"
                  type="text"
                  value={formData.color}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Main color (e.g., Red)"
                  className={`${coreStyles.inputField} ${coreStyles.inputFieldFull}`}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Material, Size */}
          <div className={coreStyles.fieldRow}>
            {/* Material */}
            <div className={`${coreStyles.fieldContainer} ${coreStyles.materialFieldContainer} ${listingMode === 'existing' ? coreStyles.fieldContainerExisting : ''}`}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>Material</div>
              </div>
              <div className={coreStyles.inputContainer}>
                <input
                  name="material"
                  type="text"
                  value={formData.material}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Material (e.g., Zinc)"
                  className={`${coreStyles.inputField} ${coreStyles.inputFieldFull}`}
                />
              </div>
            </div>

            {/* Size */}
            <div className={`${coreStyles.fieldContainer} ${coreStyles.sizeFieldContainer} ${listingMode === 'existing' ? coreStyles.fieldContainerExisting : ''}`}>
              <div className={coreStyles.labelContainer}>
                <div className={coreStyles.labelText}>Size</div>
              </div>
              <div className={coreStyles.inputContainer}>
                <input
                  name="size"
                  type="text"
                  value={formData.size}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="e.g., Large, 10x5cm"
                  className={`${coreStyles.inputField} ${coreStyles.inputFieldFull}`}
                />
              </div>
            </div>

            {/* EAN/UPC - Only for new mode */}
            {listingMode === 'new' && (
              <div className={coreStyles.eanUpcContainer}>
                <div className={coreStyles.labelContainer}>
                  <div className={coreStyles.labelText}>Do you have EAN/UPC?</div>
                </div>
                <div className={coreStyles.eanUpcInputWrapper}>
                  <div className={coreStyles.radioGroup} onClick={() => setHasEAN(true)}>
                    <div className={coreStyles.radioButton}>
                      {hasEAN === true && <div className={coreStyles.radioButtonInner}></div>}
                    </div>
                    <div className={coreStyles.radioLabel}>Yes</div>
                  </div>
                  <div className={coreStyles.radioGroup} onClick={() => {
                    setHasEAN(false);
                    handleChange({ target: { name: 'eanNumber', value: '' } });
                  }}>
                    <div className={coreStyles.radioButton}>
                      {hasEAN === false && <div className={coreStyles.radioButtonInner}></div>}
                    </div>
                    <div className={coreStyles.radioLabel}>No</div>
                  </div>
                  {hasEAN === true && (
                    <div className={coreStyles.eanInputContainer}>
                      <input
                        name="eanNumber"
                        type="text"
                        value={formData.eanNumber}
                        onChange={handleChange}
                        disabled={isLoading}
                        placeholder="Enter code"
                        className={coreStyles.eanInput}
                        maxLength={13}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* GTIN Exemption - Only show when EAN/UPC is No */}
          {listingMode === 'new' && hasEAN === false && (
            <div className={coreStyles.gtinExemptionSection}>
              <div className={coreStyles.gtinExemptionText}>Do you have GTIN Exemption?</div>
              <div className={coreStyles.radioGroupRow}>
                <div className={coreStyles.radioGroup} onClick={() => setHasGTIN(true)}>
                  <div className={coreStyles.radioButton}>
                    {hasGTIN === true && <div className={coreStyles.radioButtonInner}></div>}
                  </div>
                  <div className={coreStyles.radioLabel}>Yes</div>
                </div>
                <div className={coreStyles.radioGroup} onClick={() => setHasGTIN(false)}>
                  <div className={coreStyles.radioButton}>
                    {hasGTIN === false && <div className={coreStyles.radioButtonInner}></div>}
                  </div>
                  <div className={coreStyles.radioLabel}>No</div>
                </div>
              </div>
              {hasGTIN !== true && (
                <a
                  href="https://www.amazon.com/gp/help/customer/display.html?nodeId=200426310"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={coreStyles.gtinExemptionLink}
                >
                  Click here to apply
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.5 7.5V11C10.5 11.1326 10.4473 11.2598 10.3536 11.3536C10.2598 11.4473 10.1326 11.5 10 11.5H3C2.86739 11.5 2.74021 11.4473 2.64645 11.3536C2.55268 11.2598 2.5 11.1326 2.5 11V4C2.5 3.86739 2.55268 3.74021 2.64645 3.64645C2.74021 3.55268 2.86739 3.5 3 3.5H6.5M8.5 2.5H11.5M11.5 2.5V5.5M11.5 2.5L6 8" stroke="#737373" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              )}
            </div>
          )}

          {/* Keywords Section */}
          <div className={coreStyles.fullWidthSection}>
            <div className={coreStyles.labelContainer}>
              <div className={coreStyles.labelText}>
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.7">
<path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5"/>
<path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5"/>
</g>
</svg>
 Add Keywords (optional)</div>
            </div>
            <div className={coreStyles.keywordsContainer}>
              {keywordTags.map((tag, index) => (
                <div key={index} className={coreStyles.keywordChip}>
                  <span>{tag}</span>
                  <button
                    type="button"
                    className={coreStyles.removeChip}
                    onClick={() => removeKeywordTag(tag)}
                    disabled={isLoading}
                  >
               <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4.7L11.3 4L8 7.3L4.7 4L4 4.7L7.3 8L4 11.3L4.7 12L8 8.7L11.3 12L12 11.3L8.7 8L12 4.7Z" fill="#505050"/>
              </svg>
                  </button>
                </div>
              ))}
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={addKeywordTag}
                onBlur={commitKeywordTags}
                disabled={isLoading}
                placeholder={keywordTags.length === 0 ? "Type words your customer would search for" : ""}
                className={coreStyles.keywordInput}
              />
            </div>
          </div>

          {/* Banned Keywords Section */}
          <div className={coreStyles.fullWidthSection}>
            <div className={coreStyles.labelContainer}>
              <div className={coreStyles.labelText}>Words to Avoid (optional)</div>
            </div>
            <div className={coreStyles.keywordsContainer}>
              {bannedKeywordTags.map((tag, index) => (
                <div key={index} className={coreStyles.bannedKeywordChip}>
                  <span>{tag}</span>
                  <button
                    type="button"
                    className={coreStyles.removeChip}
                    onClick={() => removeBannedKeywordTag(tag)}
                    disabled={isLoading}
                  >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4.7L11.3 4L8 7.3L4.7 4L4 4.7L7.3 8L4 11.3L4.7 12L8 8.7L11.3 12L12 11.3L8.7 8L12 4.7Z" fill="#505050"/>
                </svg>
                  </button>
                </div>
              ))}
              <input
                type="text"
                value={bannedKeywordInput}
                onChange={(e) => setBannedKeywordInput(e.target.value)}
                onKeyDown={addBannedKeywordTag}
                onBlur={commitBannedKeywordTags}
                disabled={isLoading}
                placeholder={bannedKeywordTags.length === 0 ? "Mention words banned by amazon or you personally want to avoid" : ""}
                className={coreStyles.keywordInput}
              />
            </div>
          </div>
        </div>
    </div>
  );
};

export default DetailsForm;